import React, { Component, useEffect, useState } from 'react';
import * as d3 from 'd3';
import Tab from "./selector/tab"
import standardDna from '../../../data/dna.js';
import codVRaw from '../../../data/R9-annotated_small.tsv'
import sampleConfig from '../../../data/sample_config'

function Selector(props){

    const format = props.format,
        filename = props.filename,
        setFilename = props.setFilename,
        extra = props.extra,
        setExtra = props.setExtra,
        getExtra = props.getExtra;

    const empty = {bb: undefined, beta: undefined, pip: undefined, af: undefined}

    const [step,setStep] = useState("Position")

    const [pos,setPos] = useState(undefined) 
    const [chr,setChr] = useState(undefined)
    const [refV,setRefV] = useState(undefined)
    const [altV,setAltV] = useState(undefined)
    const [AF,setAF] = useState(undefined)
    const [bbN, setBBN] = useState(1)
    const [tracks,setTracks] = useState([empty])

    var last = tracks.length-1
    const mandatory = pos !== undefined && chr !== undefined && refV !== undefined && altV !== undefined && tracks.length > 0 && tracks[0].bb !== undefined

    var variableList = extra.af ? [
        {name: 'Position',          id: 'confirmedPos', content: pos},
        {name: 'Chromosome',        id: 'confirmedChr', content: chr},
        {name: 'Reference',         id: 'confirmedRef', content: refV},
        {name: 'Alternative',       id: 'confirmedAlt', content: altV},
    ] : [
        {name: 'Position',          id: 'confirmedPos', content: pos},
        {name: 'Chromosome',        id: 'confirmedChr', content: chr},
        {name: 'Reference',         id: 'confirmedRef', content: refV},
        {name: 'Alternative',       id: 'confirmedAlt', content: altV},
        {name: 'AF',                id: 'confirmedAf',  content: AF},
    ]

    function updateSelection (newVar) {
        switch(step){
            case "Position":  
                setPos(newVar);
                setStep('Chromosome');
                setBBN(1)
                break;
            case "Chromosome":
                setChr(newVar);
                setStep('Reference');
                setBBN(1)
                break;
            case "Reference":
                setRefV(newVar);
                setStep('Alternative');
                setBBN(1)
                break;
            case "Alternative":
                setAltV(newVar);
                setStep(extra.af ? 'Biobank 1' : 'AF');
                setBBN(1)
                break;
            case "AF":
                setAF(newVar);
                setStep('Biobank 1');
                setBBN(1)
                break;
            case "Biobank "+bbN:
                const tracks_1 = JSON.parse(JSON.stringify(tracks));
                tracks_1[bbN-1].bb = newVar;
                setTracks(tracks_1);
                setStep('Beta '+bbN);
                setBBN(bbN)
                break;
            case "Beta "+bbN:
                const tracks_2 = [...tracks]; 
                tracks_2[bbN-1].beta = newVar;
                if (extra.pip) {
                    setTracks(tracks_2)
                    setStep('PIP '+bbN)
                    setBBN(bbN)
                } 
                else if (extra.af) {
                    setTracks(tracks_2)
                    setStep('AF '+bbN)
                    setBBN(bbN)
                }
                else {
                    if (tracks[last].bb !== undefined && tracks[last].beta !== undefined) tracks_2.push(empty)
                    setTracks(tracks_2);
                    setBBN(bbN+1)
                    setStep('Biobank '+(bbN+1))
                }
                break;
            case "PIP "+bbN:
                const tracks_3 = [...tracks]; 
                tracks_3[bbN-1].pip = newVar;
                if (extra.af) {
                    setTracks(tracks_3)
                    setStep('AF '+bbN)
                    setBBN(bbN)
                }
                else {
                    if (tracks[last].bb !== undefined && tracks[last].beta !== undefined) tracks_3.push(empty)
                    setTracks(tracks_3);
                    setBBN(bbN+1)
                    setStep('Biobank '+(bbN+1))
                }
                break;
            case "AF "+bbN:
                const tracks_4 = [...tracks]; 
                tracks_4[bbN-1].af = newVar;
                if (tracks[last].bb !== undefined && tracks[last].beta !== undefined && tracks[last].af !== undefined) tracks_4.push(empty)
                setTracks(tracks_4);
                setBBN(bbN+1)
                setStep('Biobank '+(bbN+1))
                break;
            default:
                console.log("Error");
        }
    }

    function resetSelection () {
        setPos(undefined);
        setChr(undefined);
        setRefV(undefined)
        setAltV(undefined)
        setAF(undefined)
        setTracks([empty]);
        setStep('Position')
        setBBN(1);
    }

    function showAlert (message) {

        d3.select('#alert')
            .style('opacity', 1)
            .html(message)

        document.addEventListener('mousemove', function (e){
            d3.select('#alert')
                .style('left', e.pageX + 'px')
                .style('top', e.pageY + 'px')
        });
    }

    function hideAlert () {
        d3.select('#alert').style('opacity', 0)
    }

    function orderData(chr,pos){
        const formattedData = props.data.map(datum=>{
            datum[pos] = parseFloat(datum[pos]);
            datum[chr] = parseFloat(datum[chr]);
            return datum;
        })
        const chromosomes = d3.groups(formattedData,d=>d[chr]);
        const orderedChr = chromosomes.sort((a,b)=>d3.ascending(a[0],b[0]));
        const sorted = orderedChr.map(g => {
            g[1].sort((a,b)=>d3.ascending(a[pos],b[pos]))
            return g
        })

        if (sorted.length < 23) {
            var totalChr = d3.range(1, 24, 1)
            var missingChr = [];
            var presentChr = [];
        
            sorted.forEach(el => presentChr.push(el[0]))
            missingChr = totalChr.filter(el => {return !presentChr.includes(el)});
            missingChr.forEach(el => sorted.push([el, []]))
            sorted.sort((a,b) => {return a[0]-b[0]})
        }
        
        return sorted;
    }

    function defineDna (data) {
        const dna = []
        for (let i = 1; i <= 23; i++) {
            const thisChr = data[i-1][1]
            const maxPos = d3.max(thisChr.filter(el => el[chr] === i), el => el[pos])
            const item = {
                chr: i,
                size: d3.max([maxPos, standardDna[i-1].size])
            }
            dna.push(item)
        }
        return dna
    }

    function defineTopLoci (biobanks, betas, PIPs, AFs) {
        const newTop = []

        biobanks.forEach((bb, index) => {
            const values = []
            let myData = [... props.data]

            function findPeak (data) {
                const valueIndex = d3.minIndex(data, d => +d[bb])
                const value = data[valueIndex]
                if (value[bb] < 1e-8){
                    values.push(value)
                    myData = myData.filter(el => el[pos] < value[pos]-1000000 || el[pos] > value[pos]+1000000)
                    if (value[chr] === 6 && value[pos] < 35000000 && value[pos] > 25000000) myData = myData.filter(el => {
                        if (el[chr] === 6) return el[pos] < 25000000 || el[pos] > 35000000
                        else return el
                    })
                    findPeak(myData)
                }
            }
            findPeak (myData)

            newTop.push({
                biobank: bb,
                beta: betas[index],
                pip: PIPs[index],
                af: AFs[index],
                values: values,
            })
        })
        return newTop
    }

    function resetParameter (parameter) {
        setStep(parameter);
        setBBN(1);
        if (parameter === 'Position') setPos(undefined)
        else if (parameter === 'Chromosome') setChr(undefined)
        else if (parameter === 'Reference') setRefV(undefined)
        else if (parameter === 'Alternative') setAltV(undefined)
        else if (parameter === 'AF') setAF(undefined)
    }

    function addTrack (array) {
        
        array.push(empty)
        setTracks(array)
        setBBN(array.length)
        setStep('Biobank '+(array.length))
    }

    function resetTrack (index, mode) {
        const tracksNew = [...tracks]
        
        if (mode === 'all') {
            tracksNew[index] = empty
            setTracks(tracksNew)
            setStep('Biobank '+(index+1))
            setBBN(index+1)
        } else if (mode === 'bb') {
            tracksNew[index].bb = undefined
            setStep('Biobank '+(index+1))
            setBBN(index+1)
        } else if (mode === 'beta') {
            tracksNew[index].beta = undefined
            setStep('Beta '+(index+1))
            setBBN(index+1)
        } else if (mode === 'pip') {
            tracksNew[index].pip = undefined
            setStep('PIP '+(index+1))
            setBBN(index+1)
        } else if (mode === 'af') {
            tracksNew[index].af = undefined
            setStep('AF '+(index+1))
            setBBN(index+1)
        } else if (mode === 'remove') {
            tracksNew.splice(index, 1)
            setStep('Biobank '+(tracksNew.length))
            setBBN(tracksNew.length)
        }

        setTracks(tracksNew)
    }

    function defineTypes(biobanks){
        const types = biobanks.map((el,i) => {
            const text = document.getElementById("range_"+i).value
            if (text === 'def') return 'pvalue'
            else if (text === 'rel') return 'pvalue'
            else if (text === 'nom') return 'nominal'
        })
        return types
    }

    function defineRanges(biobanks){
        const ranges = biobanks.map((el,i) => {
            const text = document.getElementById("range_"+i).value
            if (text === 'def') return [-18, -13, -8]
            else if (text === 'rel') return [-17, -12, -7]
            else if (text === 'nom') return [-4, -3, -2]
        })
        return ranges
    }

    function downloadConfig() {
        const list = variableList.map(el => {
            const id = el.id.replace('confirmed', '').toLowerCase()
            return {key: id, value: el.content}
        })
        const tracks_clean = [...tracks]
        if (tracks_clean[tracks.length-1].bb === undefined) tracks_clean.pop()
        
        const tracks_final = tracks_clean.map((track,i) => {return {...track, range: document.getElementById("range_"+i).value}})
        const config = [...list, {key: 'tracks', value: tracks_final}]
        
        var a = window.document.createElement('a');
        a.href = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(config));;
        a.download = 'config.json';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

    }

    function uploadConfig() {
            const reader = new FileReader();
            const dataFile = document.getElementById("b_uploadConfig_input").files[0];
            reader.readAsText(dataFile,"UTF-8")
            reader.onload = () => applyConfig(JSON.parse(reader.result))
    }

    function applyConfig (data) {
        const config = data.reduce((obj, item) => Object.assign(obj, {[item.key]: item.value}), {});
        const configTracks = [...config.tracks, empty],
            configAF = config.af === undefined,
            configPIP = config.tracks.map(el => el.pip).filter(el => el !== undefined).length !== 0,
            configExtra = {af: configAF, pip: configPIP};

        setPos(config.pos)
        setChr(config.chr)
        setRefV(config.ref)
        setAltV(config.alt)
        setAF(config.af)
        setTracks(configTracks)
        setBBN(configTracks.length)
        setStep('Biobank '+configTracks.length)
        setExtra(configExtra)

        if (config.tracks[0].range !== undefined){
            config.tracks.forEach((track,i) => {
                document.getElementById("range_"+i).value = config.tracks[i].range
            })
        }
    }

    function uploadSampleConfig(){
        setTimeout(check, 2000)
        function check() {
            if (filename === '...') {
                applyConfig(sampleConfig)
                setFilename('Sample data')
            }
        }

    }

    function confirmSelection () {
        d3.select('#b_confirmKeys').html('Checking for invalid values...')
        let checkedData = new Promise (function(resolve,reject){
            let errors = [];
            props.data.forEach((datum,i)=>{
                [chr,pos].forEach(parameter=>{
                    if (datum[parameter] === undefined) errors.push({'line': i, 'parameter': parameter, 'type': 'undefined'})
                    else if (datum[parameter] === null) errors.push({'line': i, 'parameter': parameter, 'type': 'null'})
                    else if (datum[parameter] === '') errors.push({'line': i, 'parameter': parameter, 'type': 'empty'})
                    else if (isNaN(datum[parameter])) errors.push({'line': i, 'parameter': parameter, 'type': 'isNaN'})
                });
                [refV,altV].forEach(parameter=>{
                    if (!datum[parameter].includes('A') && !datum[parameter].includes('C') && !datum[parameter].includes('G') && !datum[parameter].includes('T')) errors.push({'line': i, 'parameter': parameter, 'type': 'invalid'})
                });
            })
            if (errors.length === 0) resolve(props.data)
            else reject(errors)
        })

        checkedData.then(
            rawdata=>{
                d3.dsv("\t",codVRaw).then(dataset=>{
                    const codV = d3.groups(dataset, d=>d.chr)
                    d3.select('#b_confirmKeys').html('Generating interface...')
                    const data = orderData(chr,pos);
                    
                    var finalTracks = []
                    tracks.forEach(track => {
                        // !extra.af && track.bb !== undefined && track.beta !== undefined && finalTracks.push(track)
                        // extra.af && track.bb !== undefined && track.beta !== undefined && track.af !== undefined && finalTracks.push(track)
                        track.bb !== undefined && finalTracks.push(track)
                    })
                    
                    const biobanks = finalTracks.map(el => el.bb);
                    const betas = finalTracks.map(el => el.beta);
                    const PIPs = finalTracks.map(el => el.pip);
                    const AFs = finalTracks.map(el => el.af);

                    props.handleDataChange(data, biobanks, betas, PIPs, AFs, defineTypes(biobanks), defineRanges(biobanks), chr, pos, refV, altV, AF, defineDna(data), codV, defineTopLoci(biobanks, betas, PIPs, AFs));
                    resetSelection();
                })
            },
            errors=>{
                resetSelection();
                d3.select('#b_confirmKeys').html('Incorrect dataset');
                d3.select('#selector_message').select('p').html('Detected '+errors.length+' errors in the dataset.</br>Please, check the console.</br></br>–</br>Mac: Cmd + Alt + J</br>Windows: Ctrl + Alt + J');
                console.log(errors);
            }
        )
    }

    uploadSampleConfig()

    useEffect(()=>{

        d3.select('#selector').style('opacity', 1)

        d3.select('#root')
            .style('display', 'flex')
            .style('justify-content', 'center')
            .style('align-items', 'center')
    })

    return (
        <div id="selector" className="section">
            <div className="section_header">
                <h1>Key selector</h1>
            </div>
            <div id="selector_content" className="section_content">
                <div id="selector_header">
                    <div id="selector_instructions">
                        <p>For each parameter select one tab from the ones below. Then, select the P-values<br/>that you want to compare and match them with betas and color ranges.</p>
                        <p>If P-values are associated with differenct allele frequency, select 'Multiple AF'.</p>
                    </div>
                    <div id="selector_buttons">
                        <input 
                            id='b_uploadConfig_input'
                            className='hiddenInput'
                            type='file'
                            onChange={() => {console.log('onchange'); uploadConfig()}}
                        />
                        <label 
                            id='b_uploadConfig_label'
                            className='buttonRegular' 
                            htmlFor='b_uploadConfig_input'
                            >
                            Upload configuration file
                        </label>
                        <button 
                            id='b_extra_pip'
                            className={extra.pip ? 'buttonPrimary' : undefined}
                            onClick={()=>setExtra({af: extra.af, pip: !extra.pip})}>
                                Finemapping PIPs
                        </button>
                        <button 
                            id='b_extra_af'
                            className={extra.af ? 'buttonPrimary' : undefined}
                            onClick={()=>setExtra({af: !extra.af, pip: extra.pip})}>
                                Multiple AFs
                        </button>
                    </div>
                </div>

                <div id="selector_options">
                    {props.data.columns?.map((el,n)=>{
                        return <div 
                            key={n}
                            onClick={() => updateSelection(el)}
                            >
                                <Tab 
                                    key={n}
                                    content={el}
                                    suggestion={step}
                                    format={format}
                                    tracks={tracks}
                                    variables={variableList}
                                />
                            </div>
                        })
                    }
                </div>

                <div id="selector_boxes">
                    <div>
                        <div style={{display: 'flex', margin: '0 20px 10px 20px', justifyContent: 'center'}}>
                            <div className={(step.includes('Biobank') || step.includes('Beta') || step.includes('PIP') || step.includes('AF'))  ? 'titleInput' : 'titleInput titleInputActive'}>Parameter *</div>
                        </div>
                        {variableList.map((el, n)=>{
                            return <div 
                                id={el.id}
                                key={n}
                                className={step === el.name ? 'box boxActive' : 'box'}
                                style={{order: n, margin: '7px 20px 7px 20px'}}
                                >
                                    <div>{el.name}</div>
                                    <button 
                                        className={step === el.name ? 'tabInput tabInputActive' : "tabInput"}
                                        onMouseEnter={()=> el.content && showAlert('Reset key')}
                                        onMouseLeave={()=> hideAlert()}
                                        onClick={() => resetParameter(el.name)}
                                        >
                                            {format('ky', el.content)}
                                    </button>
                            </div>
                        })}
                    </div>
                    <div>
                        <div style={{display: 'flex', margin: '0 65px 10px 25px', justifyContent: 'flex-end'}}>
                            <div className={step.includes('Biobank') ? 'titleInput titleInputActive' : 'titleInput'} style={{width: 143}}>P-value *</div>
                            <div className={step.includes('Beta') ? 'titleInput titleInputActive' : 'titleInput'} style={{width: 143}}>Beta</div>
                            <div className={step.includes('PIP') ? 'titleInput titleInputActive' : 'titleInput'} style={{display: !extra.pip && 'none', width: 143}}>Finemapping PIP</div>
                            <div className={step.includes('AF') ? 'titleInput titleInputActive' : 'titleInput'} style={{display: !extra.af && 'none', width: 143}}>Allele frequency</div>
                            <div className='titleInput' style={{width: 126}}>Color ranges *</div>
                        </div>
                            {tracks.map((el,n)=>{return <div className='outerBox' style={{margin: '7px 20px 7px 20px'}}>
                                <form
                                    className={step === 'Biobank '+(n+1) || step === 'Beta '+(n+1) || step === 'PIP '+(n+1) || step === 'AF '+(n+1) ? 'box boxActive' : 'box'}
                                    style={{
                                        width: getExtra(750, 610, 460), 
                                        display: 'flex'}}
                                    key = {(n+1)}
                                    >
                                        <div 
                                            style={{width: 70}}
                                            // onMouseEnter={()=> el.bb && showAlert('Reset keys.')}
                                            // onMouseLeave={()=> hideAlert()}
                                            onClick={() => resetTrack(n, 'all')}
                                            >
                                                {n+1}
                                        </div>
                                        <div style={{display: 'flex'}}>
                                            <div 
                                                key={4}
                                                className={step === 'Biobank '+(n+1) ? 'tabInput tabInputActive' : "tabInput"}
                                                onMouseEnter={()=> el.bb && showAlert('Reset P-value')}
                                                onMouseLeave={()=> hideAlert()}
                                                onClick={() => resetTrack(n, 'bb')}
                                                >
                                                    {format('ky', el.bb)}
                                            </div>
                                            <div 
                                                key={3}
                                                className={step === 'Beta '+(n+1) ? 'tabInput tabInputActive' : "tabInput"}
                                                onMouseEnter={()=> el.beta && showAlert('Reset beta')}
                                                onMouseLeave={()=> hideAlert()}
                                                onClick={() => resetTrack(n, 'beta')}
                                                >
                                                    {format('ky', el.beta)}
                                            </div>
                                            <div 
                                                key={2}
                                                style={{display: !extra.pip && 'none'}}
                                                className={step === 'PIP '+(n+1) ? 'tabInput tabInputActive' : "tabInput"}
                                                onMouseEnter={()=> el.beta && showAlert('Reset PIP')}
                                                onMouseLeave={()=> hideAlert()}
                                                onClick={() => resetTrack(n, 'pip')}
                                                >
                                                    {format('ky', el.pip)}
                                            </div>
                                            <div 
                                                key={1}
                                                style={{display: !extra.af && 'none'}}
                                                className={step === 'AF '+(n+1) ? 'tabInput tabInputActive' : "tabInput"}
                                                onMouseEnter={()=> el.af && showAlert('Reset AF')}
                                                onMouseLeave={()=> hideAlert()}
                                                onClick={() => resetTrack(n, 'af')}
                                                >
                                                    {extra.af && format('ky', el.af)}
                                            </div>
                                            <select 
                                                name={'range_'+n}
                                                id={'range_'+n}
                                                className="tabOption"
                                                >
                                                <option value='def'>Default GWS</option>
                                                <option value='rel'>Relaxed GWS</option>
                                                <option value='nom'>Nominal</option>
                                            </select>
                                        </div>
                                    </form>
                                    <div>
                                        <button 
                                            className={tracks.length===1 ? 'buttonSmall buttonInactive' : 'buttonSmall'}
                                            onClick={() => resetTrack(n, 'remove')}
                                            onMouseEnter={()=> showAlert('Remove track')}
                                            onMouseLeave={()=> hideAlert()}
                                            >
                                                –
                                        </button>
                                    </div>
                                </div>
                            })}
                            <div style={{display: 'flex', justifyContent: 'center'}}>
                                <button onClick={() => addTrack([...tracks])}>
                                    + Add track
                                </button>
                            </div>
                    </div>
                </div>
                <div id="selector_info">
                    <p>* Mandatory fields.</p>
                </div>
                <div id="selector_buttons">
                    <button 
                        onClick ={() => resetSelection()}
                        >
                            Reset keys
                    </button>
                    <button 
                        className={!mandatory ? 'buttonInactive': undefined}
                        onClick ={() => downloadConfig()}
                        >
                            Download configuration file
                    </button>
                    <button id='b_confirmKeys'
                        className={!mandatory ? 'buttonInactive': 'buttonPrimary'}
                        onClick={() => confirmSelection()}
                        >
                            Confirm keys
                    </button>
                </div>
                <div id='selector_message'>
                    <p></p>
                </div>
            </div>
            <div id="alert"></div>
        </div>
    )
}
export default Selector;